import React, { useContext, useEffect } from 'react'
import { Modal, Form, Input, Select, Button, Space } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import { GET_DEPARTMENTS } from '../graphql/Queries'
import { GET_USERS_FOR_SELECT } from 'modules/Users/graphql/Queries'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserSave } from 'utils/User'

const { Option } = Select

const TeamModal = ({ visible, onCancel, onOk, editingTeam, teams }) => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const hasEdit = canUserEdit(userPermissions || currentUser, isClient)
  const hasAdd = canUserAdd(userPermissions || currentUser, isClient)
  const hasSave = canUserSave(userPermissions || currentUser, isClient)
  const canSubmit = editingTeam ? (hasEdit && hasSave) : (hasAdd && hasSave)

  const [form] = Form.useForm()
  const { data: deptData, loading: deptLoading } = useQuery(GET_DEPARTMENTS)
  const { data: userData, loading: userLoading } = useQuery(GET_USERS_FOR_SELECT, {
    variables: {
      where: { isActive: 'true', role: ['STAFF', 'SALES_EXECUTIVE', 'ADMIN'] },
      skip: 0,
      limit: 100000,
    },
  })
  const departments = deptData?.getDepartments || []
  const users = userData?.getUsers?.users || []

  let initialValues = editingTeam
    ? {
        ...editingTeam,
        departmentId: editingTeam.department?.id,
        teamLeaderId: editingTeam.teamLeader?.id,
      }
    : {}
  useEffect(() => {
    if (editingTeam) {
      form.setFieldsValue(initialValues)
    } else {
      form.resetFields()
    }
  }, [editingTeam, form, initialValues])

  const handleSubmit = () => {
    if (!canSubmit) return
    form.validateFields().then((values) => {
      onOk(values)
    })
  }

  return (
    <Modal
      title={editingTeam ? 'Edit Team' : 'Add Team'}
      visible={visible}
      onCancel={() => {
        form.resetFields()
        onCancel()
      }}
      footer={null}
    >
      <Form
        form={form}
        layout='vertical'
        initialValues={initialValues ? initialValues : {}}
      >
        <Form.Item
          label='Department'
          name='departmentId'
          rules={[{ required: true, message: 'Please select department' }]}
        >
          <Select
            showSearch
            loading={deptLoading}
            placeholder='Select Department'
            optionFilterProp='children'
            filterOption={(input, option) =>
              String(option?.children)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            allowClear
          >
            {departments.map((dept) => (
              <Option value={dept.id} key={dept.id}>
                {dept.departmentName}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label='Team Name'
          name='teamName'
          rules={[
            { required: true, message: 'Please enter team name' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value) return Promise.resolve()
                const trimmedValue = value.trim().toLowerCase()

                const isDuplicate = teams.some(
                  (team) =>
                    team.teamName.trim().toLowerCase() === trimmedValue &&
                    (!editingTeam || team.id !== editingTeam.id)
                )

                return isDuplicate
                  ? Promise.reject(new Error('Team name already exists'))
                  : Promise.resolve()
              },
            }),
          ]}
        >
          <Input placeholder='Enter team name' />
        </Form.Item>

        <Form.Item
          label='Team Leader'
          name='teamLeaderId'
          rules={[{ required: true, message: 'Please select team leader' }]}
        >
          <Select
            showSearch
            loading={userLoading}
            placeholder='Select Team Leader'
            optionFilterProp='children'
            filterOption={(input, option) => {
              const user = users.find((u) => u.id === option.value)
              if (!user) return false
              const searchText = `${user.firstName} ${
                user.lastName ? user?.lastName : ''
              } ${user.role}`.toLowerCase()
              return searchText.includes(input.toLowerCase())
            }}
            allowClear
          >
            {users.map((user) => (
              <Option value={user.id} key={user.id}>
                <span
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>
                    {user.firstName} {user.lastName ? user?.lastName : ''}
                  </span>
                  <span
                    style={{
                      marginLeft: 8,
                      color: '#999',
                      fontStyle: 'italic',
                    }}
                  >
                    ({user.role})
                  </span>
                </span>
              </Option>
            ))}
          </Select>
        </Form.Item>
        {/* <Form.Item label='Current Load Score' name='currentLoadScore'>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item> */}
        <Form.Item>
          <Space
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              onClick={() => {
                form.resetFields()
                onCancel()
              }}
            >
              Cancel
            </Button>
            <Button
              type='primary'
              onClick={handleSubmit}
              disabled={
                !canSubmit ||
                form
                  .getFieldsError()
                  .some(({ errors }) => errors.length)
              }
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default TeamModal
